import { Component, Input, OnInit } from '@angular/core';
import { finalize, forkJoin, Observable, of, switchMap } from 'rxjs';
import { LocalProfileService } from '../../../../services/local-profile.service';
import { SeoWorkService } from '../../../../services/seo-work.service';
import { Skus } from 'app/models/enums/Skus';

@Component({
  selector: 'app-components-shared-local-profile-initialization',
  templateUrl: './local-profile-initialization.component.html'
})
export class LocalProfileInitializationComponent implements OnInit {
  private readonly maxLocalProfiles = 20;

  @Input() customer: Boo.Objects.Customer;

  localProfileInitialization: Boo.Objects.LocalProfiles.LocalProfileInitialization;
  localProfileRequirements: Boo.LocalProfiles.Models.LocalProfileRequirements;
  localProfileQtyNumbers: number[] = Array.from({ length: this.maxLocalProfiles + 1 }, (_, i) => i);
  subscriptionQtyNumbers: number[] = [];
  private previousNumberOfLocalProfiles = 0;
  private previousInitializationNote;

  constructor(
    private localProfileService: LocalProfileService,
    private seoWorkService: SeoWorkService) { }

  ngOnInit(): void {
    this.load()
      .subscribe({ error: (err) => toastr.error(err) });
  }

  load(): Observable<void> {
    return forkJoin({
      initialization: this.localProfileService.getOpenInitialization(this.customer.CustomerId, Skus.SEOMinute),
      localProfileRequirements: this.localProfileService.getRequirements(this.customer.CustomerId),
      currentPeriodDetails: this.seoWorkService.getCurrentPeriodDetails(this.customer.CustomerId)
    }).pipe(
      switchMap(result => {
      this.localProfileRequirements = result.localProfileRequirements;
      // If BPM exists in the current than the user cannot add subscriptions for SEOMinute
      // We do not allow both subscriptions for BPM and SEO. Must have one or the other.
      if (result.currentPeriodDetails.BusinessProfileManagementQuantity > 0) {
        this.localProfileRequirements.MaxSubscriptions = 0;
      }

      this.localProfileInitialization = result.initialization ?? {
        NumberOfSubscriptions: this.localProfileRequirements.MaxSubscriptions > 0 ? 1 : 0,
        NumberOfLocalProfiles: this.localProfileRequirements.MaxSubscriptions > 0 ? 1 : 0,
        Sku: Skus.SEOMinute,
        Note: '',
        CustomerId: this.customer.CustomerId,
        InitializedDate: null,
        InsertedDate: null,
        LocalProfileInitializationId: 0
      };

      this.previousNumberOfLocalProfiles = this.localProfileInitialization.NumberOfLocalProfiles;

      this.updateSubscriptionOptions();

      return of(null);
    }));
  }

  get noteDisabled(): boolean {
    return this.localProfileInitialization.NumberOfLocalProfiles == 0;
  }
  
  onQtyChange(): void {
    const currentNumberOfLocalProfiles = this.localProfileInitialization.NumberOfLocalProfiles;
    const currentNumberOfSubscriptions = this.localProfileInitialization.NumberOfSubscriptions;

    const shouldAutoAdjust =
      currentNumberOfLocalProfiles > this.previousNumberOfLocalProfiles && currentNumberOfSubscriptions === this.previousNumberOfLocalProfiles;

    if (shouldAutoAdjust) {
      this.localProfileInitialization.NumberOfSubscriptions = Math.min(currentNumberOfLocalProfiles, this.localProfileRequirements.MaxSubscriptions);
    }

    this.previousNumberOfLocalProfiles = currentNumberOfLocalProfiles;

    this.updateSubscriptionOptions();

    if (currentNumberOfLocalProfiles === 0) {
      this.previousInitializationNote = this.localProfileInitialization.Note;
      this.localProfileInitialization.Note = '';
    } else if (!this.localProfileInitialization.Note && this.previousInitializationNote) {
      this.localProfileInitialization.Note = this.previousInitializationNote;
      this.previousInitializationNote = undefined;
    }
  }
  
  save(): Observable<void> {
    return this.localProfileService.saveInitialization(this.localProfileInitialization)
  }

  private updateSubscriptionOptions(): void {
    const NumberOfLocalProfiles = this.localProfileInitialization.NumberOfLocalProfiles;
    const NumberOfSubscriptions = this.localProfileInitialization.NumberOfSubscriptions;
    const maxSubscriptions = this.localProfileRequirements.MaxSubscriptions;

    const maxAllowedSubs = Math.min(NumberOfLocalProfiles, maxSubscriptions);
    this.subscriptionQtyNumbers = Array.from({ length: maxAllowedSubs + 1 }, (_, i) => i);

    if (NumberOfSubscriptions > maxAllowedSubs) {
      this.localProfileInitialization.NumberOfSubscriptions = NumberOfLocalProfiles;
    }
  }
}